import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_CONTACT_EMAIL,
  DEFAULT_WHATSAPP_DISPLAY,
  absoluteUrl,
  whatsappToWaDigits
} from './siteConfig';

export const SCHEMA_CONTEXT = 'https://schema.org';
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/** JSON-LD — tek script veya @graph */
export function toJsonLd(schemas) {
  if (!schemas || (Array.isArray(schemas) && schemas.length === 0)) return null;
  const list = (Array.isArray(schemas) ? schemas : [schemas]).filter(Boolean);
  const graph = list.map((node) => {
    if (!node || typeof node !== 'object') return node;
    const { '@context': _ctx, ...rest } = node;
    return rest;
  });
  if (graph.length === 1) {
    return { '@context': SCHEMA_CONTEXT, ...graph[0] };
  }
  return { '@context': SCHEMA_CONTEXT, '@graph': graph };
}

export function buildOrganizationSchema(contactInfo = null) {
  const email = contactInfo?.email || DEFAULT_CONTACT_EMAIL;
  const phoneDisplay = contactInfo?.whatsapp || DEFAULT_WHATSAPP_DISPLAY;
  const digits = whatsappToWaDigits(phoneDisplay);
  const telephone = digits.startsWith('+') ? digits : `+${digits}`;

  const schema = {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: absoluteUrl(DEFAULT_OG_IMAGE),
    image: absoluteUrl(DEFAULT_OG_IMAGE),
    description:
      `${SITE_NAME}; Türkiye ve Avrupa için IPTV satın alma, paketler, kurulum rehberi ve WhatsApp destek hattı.`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone,
      email,
      contactType: 'customer service',
      areaServed: ['TR', 'DE', 'EU'],
      availableLanguage: ['Turkish', 'English', 'German']
    }
  };

  const addr = contactInfo?.address;
  if (addr && (addr.street || addr.city || addr.country)) {
    schema.address = {
      '@type': 'PostalAddress',
      ...(addr.street ? { streetAddress: addr.street } : {}),
      ...(addr.city ? { addressLocality: addr.city } : {}),
      ...(addr.postalCode ? { postalCode: addr.postalCode } : {}),
      ...(addr.country ? { addressCountry: addr.country } : {})
    };
  }

  return schema;
}

export function buildWebSiteSchema() {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE_URL,
    name: SITE_NAME,
    description:
      'IPTV paketleri, fiyatlar, kurulum adımları ve sık sorulan sorular. TR ve EU bölgeleri için canlı TV, film ve dizi.',
    inLanguage: ['tr-TR', 'de-DE', 'en-US'],
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/paketler?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
}

/** Sayfa breadcrumb: [{ name, path }] — path örn. `/paketler` */
export function breadcrumbSchema(crumbs) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path}`
    }))
  };
}

export function webPageSchema({ name, description, path }) {
  return {
    '@type': 'WebPage',
    '@id': `${SITE_URL}${path}#webpage`,
    url: `${SITE_URL}${path}`,
    name,
    description,
    inLanguage: ['tr-TR', 'de-DE', 'en-US'],
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID }
  };
}

export function buildPageGraph(pageNodes, contactInfo = null) {
  return toJsonLd([
    buildOrganizationSchema(contactInfo),
    buildWebSiteSchema(),
    ...(Array.isArray(pageNodes) ? pageNodes : [pageNodes])
  ]);
}
